import { Component, ElementRef, Injector, OnInit, Renderer2, resolveForwardRef } from '@angular/core';
import { SelectWidget } from '@app/finxone-web-frontend/app/lib/actions/grid.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { ResponsiveHideConditionsService } from '@app/finxone-web-frontend/app/lib/services/responsive-hide-conditions-service/responsive-hide-conditions.service';
import { getComponentByLabel } from '@app/finxone-web-frontend/app/lib/utils/get-component.utils';
import { getWidgetClasses } from '@app/finxone-web-frontend/app/widget.utis';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  NewUiZoneWidgetConfig,
  ProviderClass,
  cssToNgStyle,
  is2dArray,
  isLayoutWidget,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Subject, takeUntil } from 'rxjs';
import { createChildWidgetInjector } from '../../zones/utils';
import { ChildComponentList } from '../../zones/widget-layout/main-widget/main-widget.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-section-widget',
  template: `
    <section [ngStyle]="outerCss">
      <ng-container *ngFor="let component of injectorArray">
        @if(configService.getActiveConfigType().isWorkflow) {
        <div
          [ngClass]="component.widget.uniqueId === mouseOverId ? mouseOver : ''"
          (click)="selectWidget($event, component.widget)"
          (mouseover)="changeStyle($event, component.widget.uniqueId.toString())"
          (mouseout)="changeStyle($event)"
        >
          <div [class]="getWidgetClasses(component.widget)">
            <ng-container
              *ngComponentOutlet="component.component; injector: component.injector"
            ></ng-container>
          </div>
        </div>
        } @else {
        <div [class]="getWidgetClasses(component.widget)">
          <ng-container *ngComponentOutlet="component.component; injector: component.injector"></ng-container>
        </div>
        }
      </ng-container>
    </section>
  `,
})
export class SectionWidgetComponent implements OnInit {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  injectorComponents: {
    [key: string]: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      component: any;
      injector: Injector;
      widget: NewUiZoneWidgetConfig;
    };
  } = {};
  private isWorkflowType: boolean;

  get injectorArray() {
    return Object.values(this.injectorComponents);
  }

  mouseOver = '';
  mouseOverId = '';
  changeStyle($event: any, mouseOverId?: string) {
    this.mouseOver = $event.type == 'mouseover' ? 'widget-hover' : '';
    if (mouseOverId) this.mouseOverId = mouseOverId;
    $event.stopPropagation();
  }
  selectWidget(event: MouseEvent, widget: NewUiZoneWidgetConfig) {
    if (this.isWorkflowType) {
      if (event.shiftKey) {
        this.store.dispatch(new SelectWidget(widget.uniqueId.toString(), true));
      } else {
        this.store.dispatch(new SelectWidget(widget.uniqueId.toString(), false));
      }
    }
    event.stopPropagation();
  }

  constructor(
    public childComponents: ChildComponentList,
    private dataIn: ProviderClass<object>,
    private injector: Injector,
    public configService: ConfigService,
    private store: Store,
    private responsiveHideConditionsService: ResponsiveHideConditionsService,
    private elRef: ElementRef,
    private renderer: Renderer2,
  ) {}

  get outerCss() {
    return cssToNgStyle(this.dataIn.widgetConfig.attributes.outerCss ?? '');
  }

  getWidgetClasses = getWidgetClasses;

  ngOnInit(): void {
    if (this.dataIn.widgetConfig.attributes?.['usingResponsiveHideCondition']) {
      this.setupResponsiveHideConditionSubscription();
    }
    if (is2dArray(this.childComponents.list)) {
      this.childComponents.list as NewUiZoneWidgetConfig[][];
      throw new Error('Section widget cannot have multiple child component arrays');
    } else {
      const list = this.childComponents.list as NewUiZoneWidgetConfig[];
      list.forEach((widget) => {
        const component = resolveForwardRef(getComponentByLabel(widget.name));
        this.injectorComponents[widget.uniqueId] = {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          component: component,
          injector: createChildWidgetInjector(
            widget,
            this.injector,
            this.dataIn,
            isLayoutWidget(widget.name)
              ? [
                  {
                    provide: ChildComponentList,
                    useValue: new ChildComponentList(widget.childComponents ?? []),
                  },
                ]
              : [],
          ),
          widget: widget,
        };
      });
    }
    this.isWorkflowType = this.configService.getActiveConfigType()?.isWorkflow;
  }

  private setupResponsiveHideConditionSubscription(): void {
    this.responsiveHideConditionsService
      .setupHideCondition(this.dataIn.widgetConfig.attributes['responsiveHideCondition'])
      .pipe(takeUntil(this.destroy$))
      .subscribe((shouldHide) => {
        const hostEl = this.elRef.nativeElement as HTMLElement;
        if (!hostEl) return;
        const parentEl = hostEl.parentElement;
        if (!parentEl) return;
        if (shouldHide) {
          this.renderer.addClass(parentEl, 'd-none');
        } else {
          this.renderer.removeClass(parentEl, 'd-none');
        }
      });
  }
}
